import { useEffect, useState } from "react";

type NetworkConnection = {
  effectiveType?: string;
  addEventListener?: (type: string, listener: () => void) => void;
  removeEventListener?: (type: string, listener: () => void) => void;
};

type NavigatorWithConnection = Navigator & {
  connection?: NetworkConnection;
};

export type NetworkStatus = {
  isOnline: boolean;
  isSlowConnection: boolean;
};

const SLOW_CONNECTION_TYPES = new Set(["slow-2g", "2g"]);

function readNetworkStatus(): NetworkStatus {
  const navigatorWithConnection = navigator as NavigatorWithConnection;
  const effectiveType = navigatorWithConnection.connection?.effectiveType;

  return {
    isOnline: navigator.onLine,
    isSlowConnection: effectiveType ? SLOW_CONNECTION_TYPES.has(effectiveType) : false,
  };
}

export function useNetworkStatus(): NetworkStatus {
  const [status, setStatus] = useState<NetworkStatus>(readNetworkStatus);

  useEffect(() => {
    const updateStatus = () => {
      setStatus(readNetworkStatus());
    };

    window.addEventListener("online", updateStatus);
    window.addEventListener("offline", updateStatus);

    const connection = (navigator as NavigatorWithConnection).connection;
    connection?.addEventListener?.("change", updateStatus);

    return () => {
      window.removeEventListener("online", updateStatus);
      window.removeEventListener("offline", updateStatus);
      connection?.removeEventListener?.("change", updateStatus);
    };
  }, []);

  return status;
}
